import BreadcrumbsComponent from '@/components/Breadcrumbs';
import ShimmerLoader from '@/components/common/ShimmerLoader';
import MessageModal from '@/components/common/modals/MessageModal';
import ShimmerDataGrid from '@/components/common/shimmer-data-grid';
import AddTurnAroundModal from '@/components/turnaround/AddTurnAroundModal';
import DeleteTurnAroundConfirm from '@/components/turnaround/DeleteTurnAroundConfirm';
import TurnAroundHeader from '@/components/turnaround/TurnAroundHeader';
import TurnaroundGrid from '@/components/turnaround/TurnaroundGrid';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectSelectedSiteId } from '@/store/selectors';
import { fetchTurnarounds } from '@/store/slices/turnaroundsSlice';
import Grid from '@mui/material/Grid';
import type { GridPaginationModel } from '@mui/x-data-grid';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const TurnAround = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { getAccessTokenSilently } = useAuth0();
  
  // Redux selectors
  const selectedSiteId = useAppSelector(selectSelectedSiteId);
  const { data: userData } = useAppSelector((state) => state.user);
  const { status, fetched } = useAppSelector((state) => state.turnarounds);

  // Shared pagination state
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100,
  });

  // Page-level UI state only
  const [filters, setFilters] = useState<any>(null);
  const [editingTurnaround, setEditingTurnaround] = useState<any>(null);
  const [addTurnaroundModalOpen, setAddTurnaroundModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [turnaroundToDelete, setTurnaroundToDelete] = useState<Array<{ id: string; name: string }> | null>(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // Centralized fetch function - only place that fetches turnarounds data
  const fetchData = useCallback(async () => {
    if (selectedSiteId && userData?.id) {
      try {
        const accessToken = await getAccessTokenSilently();        
        dispatch(fetchTurnarounds({ 
          userId: userData.id, 
          siteId: selectedSiteId,
          accessToken: accessToken
        }));
      } catch (error) {
        console.error('Failed to fetch turnarounds:', error);
      }
    }
  }, [selectedSiteId, userData?.id, dispatch]);

  // Fetch data whenever component mounts or dependencies change
  // This ensures fresh data when navigating back to the page
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handlers for grid callbacks
  const handleEditTurnaround = (rowId: string, allRows: any[]) => {
    const rowToEdit = allRows.find(row => row.id === rowId);
    if (rowToEdit) {
      setEditingTurnaround(rowToEdit);
      setAddTurnaroundModalOpen(true);
    }
  };

  const handleDeleteTurnAround = (rowId: string, allRows: any[]) => {
    const rowToDelete = allRows.find(row => row.id === rowId);
    if (rowToDelete) {
      setTurnaroundToDelete([{ id: rowToDelete.id, name: rowToDelete.name }]);
      setDeleteConfirmOpen(true);
    }
  };

  const handleBulkDelete = (allRows: any[]) => {
    // This will be called by grid with filtered rows
    setTurnaroundToDelete(allRows);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteSuccess = async (count: number) => {
    setDeleteConfirmOpen(false);
    setTurnaroundToDelete(null);

    // Refetch data after successful deletion
    await fetchData();

    const message = count > 1
      ? t('turnaroundsDeletedSuccess', 'Turnarounds deleted successfully.')
      : t('turnaroundDeletedSuccess', 'Turnaround deleted successfully.');
    setSuccessMessage(message);
    setSuccessModalOpen(true);
  };

  const handleClose = () => {
    setAddTurnaroundModalOpen(false);
    setEditingTurnaround(null);
  };

  const handleSuccess = async () => {
    const message = editingTurnaround
      ? t('turnaroundUpdatedSuccess', 'Turnaround updated successfully.')
      : t('turnaroundCreatedSuccess', 'Turnaround created successfully.');
    setSuccessMessage(message);
    handleClose();
    setSuccessModalOpen(true);
    // Refetch data after successful create/update
    await fetchData();
  };

  const filterDevices = async (filters: any) => {
    setFilters(filters);
  };

  return (
    <div className="page page-device-list">
      <BreadcrumbsComponent />
      <div className="page-content">
        <div data-testid="device-list-root">
          <Grid container spacing={2}>
            <Grid size={12}>
              <Suspense fallback={<ShimmerLoader variant="text" lines={3} height="14px" />}>
                <div data-lazy-component="TurnAroundHeader">
                  <TurnAroundHeader
                    filterDevices={filterDevices}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    onCreateTurnaroundClick={() => setAddTurnaroundModalOpen(true)}
                  />
                </div>
              </Suspense>
            </Grid>
          </Grid>
          <Suspense fallback={<ShimmerDataGrid />}>
            <div data-lazy-component="TurnaroundGrid">
              <TurnaroundGrid
                filters={filters}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                onEditTurnaround={handleEditTurnaround}
                onDeleteTurnaround={handleDeleteTurnAround}
                onBulkDelete={handleBulkDelete}
              />
            </div>
          </Suspense>
          {addTurnaroundModalOpen && (
            <AddTurnAroundModal
              onClose={handleClose}
              onSuccess={handleSuccess}
              existingTurnaround={editingTurnaround}
            />
          )}
          {deleteConfirmOpen && turnaroundToDelete && (
            <DeleteTurnAroundConfirm
              open={deleteConfirmOpen}
              turnarounds={turnaroundToDelete}
              onClose={() => setDeleteConfirmOpen(false)}
              onSuccess={handleDeleteSuccess}
            />
          )}
          <MessageModal
            open={successModalOpen}
            onClose={() => setSuccessModalOpen(false)}
            title={t('success', 'Success')}
            message={successMessage}
          />
        </div>
      </div>
    </div>
  );
};
export default TurnAround;
